/* eslint no-underscore-dangle: ["error", { "allow": ["_root"] }] */
// need this eslint rule since griddle now produces dangling vars...

import React from 'react';
import PropTypes from 'prop-types';
import { Link, browserHistory } from 'react-router';
import InlineSVG from 'svg-inline-react';
import Griddle, {
  plugins,
  RowDefinition,
  ColumnDefinition,
} from 'griddle-react';
import ReactTooltip from 'react-tooltip';
import _ from 'lodash';
import moment from 'moment';

import IconMenu from 'material-ui/IconMenu';
import MenuItem from 'material-ui/MenuItem';
import IconButton from 'material-ui/IconButton';
import Divider from 'material-ui/Divider';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import EditIcon from 'material-ui/svg-icons/content/create';
import Blueprint from 'material-ui/svg-icons/content/content-copy';
import SubProject from 'material-ui/svg-icons/navigation/subdirectory-arrow-right';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import { Dialog, FlatButton } from 'material-ui';

import ContactCard from '../../../components/ContactCard/ContactCard';
import IconProjectOpen from '../../../assets/ic-project-open.svg';
import IconProjectClosed from '../../../assets/ic-project-closed.svg';
import IconProjectCancelled from '../../../assets/ic-project-cancelled.svg';

import caseInsensitiveSort from '../../../helpers/caseInsensitiveSort';

import classes from './Project.scss';

const renderCell = data => {
  let children = '';

  if (data.value._root.entries[3][1] !== 0) {
    children = ` (${data.value._root.entries[3][1]} Unterprojekte)`;
  }

  return (
    <Link to={`/project/${data.value._root.entries[1][1]}`}>
      {data.value._root.entries[0][1]}
      {children}
    </Link>
  );
};

const renderResponsible = data => {
  if (data.value === null) {
    return <span />;
  }

  const values = _.split(data.value, ':-:');
  const name = values[0];
  const id = values[1];
  const avatar = values[2];

  return (
    <ContactCard
      name={name}
      id={id}
      avatar={avatar}
      type="staff"
      displayType="small"
    />
  );
};

const renderCompany = data => {
  if (data.value === null) {
    return <span />;
  }

  const values = _.split(data.value, ':-:');
  const name = values[0];
  const id = values[1];

  return <Link to={`/company/${id}`}>{name}</Link>;
};

const renderDate = data => {
  if (typeof data.value === 'undefined' || data.value === null) {
    return <span />;
  }
  const rawDate = data.value;

  return <span>{moment(rawDate).format('DD.MM.YYYY')}</span>;
};

const renderStatus = data => {
  const values = _.split(data.value, ':-:');
  const intStatus = parseInt(values[0], 10);
  const projectId = values[1];

  const statusTooltipId = `project_${projectId}_status_tooltip`;
  let status = <span />;
  if (intStatus === 0) {
    status = (
      <InlineSVG
        src={IconProjectOpen}
        className={classes.projectStatusIcon}
        data-tip="offen"
        data-for={statusTooltipId}
      />
    );
  } else if (intStatus === 1) {
    status = (
      <InlineSVG
        src={IconProjectClosed}
        className={classes.projectStatusIcon}
        data-tip="abgeschlossen"
        data-for={statusTooltipId}
      />
    );
  } else if (intStatus === 2) {
    status = (
      <InlineSVG
        src={IconProjectCancelled}
        className={classes.projectStatusIcon}
        data-tip="abgebrochen"
        data-for={statusTooltipId}
      />
    );
  }

  return (
    <span className={classes.statusColumn}>
      {status}
      <ReactTooltip
        id={statusTooltipId}
        className={classes.tooltip}
        place="top"
        type="dark"
        effect="solid"
      />
    </span>
  );
};

const prepareData = (dataArray, staffs, companies) => {
  const returnData = {
    total: 0,
  };

  const newData = [];
  _.forOwn(dataArray, data => {
    // create new object that will be handled by griddle
    let leader = {};
    if (typeof staffs[data.project_leader] !== 'undefined') {
      leader = staffs[data.project_leader];
    }

    let companyData = {};
    if (typeof companies[data.company] !== 'undefined') {
      companyData = companies[data.company];
    }

    let end = null;
    if (typeof data.end !== 'undefined' && data.end !== null) {
      end = data.end;
    }

    let responsible = null;
    if (typeof leader.id !== 'undefined') {
      responsible = `${leader.name}:-:${leader.id}:-:${leader.avatar}:-:staff`;
    }

    let company = null;
    if (typeof companyData.id !== 'undefined') {
      company = `${companyData.name}:-:${companyData.id}`;
    }

    let status = null;
    if (data.status === 'open') {
      status = 0;
    } else if (data.status === 'closed') {
      status = 1;
    } else if (data.status === 'canceled') {
      status = 2;
    }
    status = `${status}:-:${data.id}`;

    if (data.id && data.deleted_at === null) {
      newData.push({
        id: data.id,
        number: data.project_number,
        title: data.name,
        name: leader.name,
        link: {
          title: data.name,
          id: data.id,
          status: data.status,
          children: data.childrenCount,
        },
        responsible,
        company,
        start: data.start,
        end,
        status,
      });

      returnData.total += 1;
    }
  });

  returnData.data = newData;
  return returnData;
};

class Project extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      alert: null,
    };
  }

  componentDidMount() {
    this.props.fetchProject();
    this.refresh = setTimeout(() => {
      this.forceUpdate();
    }, 1000);
    this.refreshSlow = setTimeout(() => {
      this.forceUpdate();
    }, 4000);
  }

  shouldComponentUpdate(nextProps, nextState) {
    let rerender = false;

    if (
      Object.keys(nextProps.staffs).length !==
      Object.keys(this.props.staffs).length
    ) {
      rerender = true;
    }

    if (
      Object.keys(nextProps.companies).length !==
      Object.keys(this.props.companies).length
    ) {
      rerender = true;
    }

    if (
      Object.keys(nextProps.projects).length !==
      Object.keys(this.props.projects).length
    ) {
      rerender = true;
    }

    if (this.props.fetching !== nextProps.fetching) {
      rerender = true;
    }

    if (this.props.fetchingStaff !== nextProps.fetchingStaff) {
      rerender = true;
    }

    if (this.state.alert !== nextState.alert) {
      rerender = true;
    }

    return rerender;
  }

  componentWillUnmount() {
    clearTimeout(this.refresh);
    clearTimeout(this.refreshSlow);
  }

  handleAlertClose = () => {
    this.setState({
      alert: null,
    });
  };

  renderOverflow = data => (
    <IconMenu
      iconButtonElement={
        <IconButton>
          <MoreVertIcon />
        </IconButton>
      }
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      targetOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <MenuItem
        primaryText="Bearbeiten"
        leftIcon={<EditIcon />}
        onTouchTap={e => this.props.setProjectForm(data.value)}
      />
      <MenuItem
        primaryText="Als Vorlage verwenden"
        leftIcon={<Blueprint />}
        onTouchTap={e => {
          window.simplPreselection.usAsBlueprint = true;
          this.props.setProjectForm(data.value);
        }}
      />
      <MenuItem
        primaryText="Neues Unterprojekt"
        leftIcon={<SubProject />}
        onTouchTap={e => {
          window.simplPreselection.project = data.value;
          this.props.setProjectForm();
        }}
      />
      <Divider />
      <MenuItem
        primaryText="Projekt löschen"
        leftIcon={<DeleteIcon className="svg-icon-primary svg-icon-warning" />}
        key="delProject"
        className="menu-primary"
        onClick={() => {
          this.setState({
            alert: (
              <Dialog
                actions={[
                  <FlatButton
                    label="Abbrechen"
                    primary
                    onTouchTap={this.handleAlertClose}
                  />,
                  <FlatButton
                    label="Löschen"
                    primary
                    onTouchTap={() => {
                      this.handleAlertClose();
                      this.props.deleteProject(data.value);
                      browserHistory.push('/project');
                    }}
                  />,
                ]}
                modal={false}
                open
                onRequestClose={this.handleAlertClose}
              >
                Projekt wirklich löschen?
              </Dialog>
            ),
          });
        }}
      />
    </IconMenu>
  );

  renderLinksMobile = dataArray => {
    const links = [];

    _.forOwn(dataArray, value => {
      links.push(
        <div key={`mobile_link_${value.id}`}>
          <Link to={`/project/${value.id}`}>{value.name}</Link>
        </div>,
      );
    });

    return links;
  };

  render() {
    const props = this.props;
    const components = {
      NoResults: () => (
        <div>
          <p className="text-center">
            <small>Keine Projekte eingetragen.</small>
          </p>
        </div>
      ),
    };

    if (window.innerWidth < 767) {
      return (
        <div className="wrapper">
          <div>
            <h2 className={`${classes.zenHeader} gutter`}>Projekte</h2>

            <div className="mobile-list">
              {this.renderLinksMobile(props.projects)}
            </div>
          </div>
        </div>
      );
    }

    const data = prepareData(props.projects, props.staffs, props.companies);
    const sortProperties = [
      {
        id: 'link',
        sortAscending: true,
      },
    ];
    return (
      <div className="wrapper">
        <h2 className={`${classes.zenHeader} gutter`}>{data.total} Projekte</h2>

        <Griddle
          data={data.data}
          plugins={[plugins.LocalPlugin]}
          pageProperties={{
            pageSize: 5000,
          }}
          components={components}
          sortProperties={sortProperties}
          sortMethod={caseInsensitiveSort}
        >
          <RowDefinition keyColumn="id">
            <ColumnDefinition
              id="id"
              title="ID"
              cssClassName="text-right"
              visible={false}
            />
            <ColumnDefinition
              id="number"
              title="PNR"
              sortMethod={caseInsensitiveSort}
            />
            <ColumnDefinition
              id="link"
              title="Titel"
              customComponent={renderCell}
              sortMethod={caseInsensitiveSort}
            />
            <ColumnDefinition
              id="responsible"
              title="Leiter"
              customComponent={renderResponsible}
              sortMethod={caseInsensitiveSort}
            />
            <ColumnDefinition
              id="company"
              title="Unternehmen"
              customComponent={renderCompany}
              sortMethod={caseInsensitiveSort}
            />
            <ColumnDefinition
              id="start"
              title="Start"
              customComponent={renderDate}
            />
            <ColumnDefinition
              id="end"
              title="Ende"
              customComponent={renderDate}
            />
            <ColumnDefinition
              id="status"
              title="Status"
              className="text-center"
              customComponent={renderStatus}
              sortMethod={caseInsensitiveSort}
            />
            <ColumnDefinition
              id="id"
              title=" "
              sortable={false}
              width={70}
              cssClassName="OverFlowButton text-right"
              customComponent={this.renderOverflow}
            />
          </RowDefinition>
        </Griddle>

        {this.state.alert}
      </div>
    );
  }
}
Project.propTypes = {
  fetchProject: PropTypes.func.isRequired,
  setProjectForm: PropTypes.func.isRequired,
  deleteProject: PropTypes.func.isRequired,
  project: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
  projects: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  staffs: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  companies: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  fetching: PropTypes.bool.isRequired,
  fetchingStaff: PropTypes.bool.isRequired,
};
export default Project;



// WEBPACK FOOTER //
// ./src/routes/Project/components/Project.js